import { useEffect, useId, useRef, useState } from 'react';
import './HelpTooltip.css';

export interface HelpTooltipProps {
  /** Kid-readable explanation. Short — one or two sentences max. */
  children: React.ReactNode;
  /** Hover-text label of the trigger button. */
  label?: string;
}

interface BubblePosition {
  top: number;
  left: number;
  /** Horizontal placement relative to the trigger — used to flip the
   *  little speech-tail arrow if we ever add one. */
  align: 'left' | 'right' | 'center';
}

/**
 * Small (?) button that opens a kid-readable help bubble. The bubble
 * is rendered with `position: fixed` and its placement is computed
 * from the trigger's bounding rect each time it opens, with a
 * viewport-clamp so it can never get cut off near a screen edge —
 * which was the bug behind the earlier "help text getting cut off"
 * report. Clicks toggle. Used throughout the advanced settings —
 * because the audience is 8-14, every advanced knob earns a one-line
 * explanation in plain words.
 */
export function HelpTooltip({
  children,
  label = 'What does this do?',
}: HelpTooltipProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<BubblePosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipId = useId();

  useEffect(() => {
    if (!open) return;
    const place = (): void => {
      const button = triggerRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      // Bubble sizing — keep the constants in sync with the CSS so the
      // viewport-clamp matches what actually renders.
      const margin = 8;
      const maxWidth = Math.min(280, window.innerWidth - margin * 2);
      // Try centering above the trigger first.
      const centered = rect.left + rect.width / 2 - maxWidth / 2;
      const clampedLeft = Math.max(
        margin,
        Math.min(window.innerWidth - maxWidth - margin, centered),
      );
      // Default: above the trigger. If there's not enough room, drop
      // below it instead (the bubble has its own max-height).
      const aboveTop = rect.top - 12; // bubble's bottom edge
      const fitsAbove = aboveTop >= 120; // give it ~120px of headroom
      const top = fitsAbove ? rect.top - 12 : rect.bottom + 12;
      const align: BubblePosition['align'] =
        Math.abs(clampedLeft - centered) < 4 ? 'center' : clampedLeft > centered ? 'left' : 'right';
      setPosition({ top, left: clampedLeft, align });
    };
    place();
    const onScroll = (): void => place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  // Close the bubble when the kid clicks anywhere outside the trigger
  // or the bubble — feels right for both touch and mouse.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      const tooltip = document.getElementById(tooltipId);
      if (tooltip?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, tooltipId]);

  return (
    <span className="bl-help">
      <button
        ref={triggerRef}
        type="button"
        className="bl-help__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label={label}
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
      >
        ?
      </button>
      {open && position && (
        <span
          id={tooltipId}
          role="tooltip"
          className="bl-help__bubble"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform:
              position.top < 100
                ? // We placed it below the trigger — no transform needed.
                  'translateY(0)'
                : // Default: above the trigger; pull bubble up by its own height.
                  'translateY(-100%)',
          }}
          data-align={position.align}
        >
          {children}
        </span>
      )}
    </span>
  );
}
